"use client";

import { useActionState, useEffect, useRef } from "react";
import { lookupLoginAudience } from "@/lib/actions/login-router";
import type { LoginRouterLookupState } from "@/lib/auth/login-router-support";
import { PORTAL_ORIGINS } from "@/lib/auth/portal-hosts";
import { SPATIALIST_APPLY_URL } from "@/lib/virtualspace/spatialist-apply-url";


function requestAccessUrl(email: string): string {
  const params = new URLSearchParams({ source: "login_router" });
  if (email) params.set("email", email);
  return `${PORTAL_ORIGINS.spacestation}/login/request-access?${params}`;
}

/**
 * Email-first router form (`app.virtualspace.xyz`, AD #158 ruling 2).
 * Forwarding is silent for a single match (auto-continue). Unknown
 * AND known-but-unissued emails share one access-help outcome; the
 * staff audience ships dark (`isStaffPointerEnabled`). Visitor copy
 * does not advertise a multi-app router.
 */
export function LoginRouterForm() {
  const [state, formAction, pending] = useActionState<
    LoginRouterLookupState,
    FormData
  >(lookupLoginAudience, { status: "idle" });
  const forwardedUrlRef = useRef<string | null>(null);

  const showForm = state.status !== "done";
  const forwarding =
    state.status === "done" &&
    state.outcome === "forward" &&
    state.targets[0] != null;

  // bfcache back-navigation can restore the “Continuing…” state and
  // re-fire the forward effect — reload so the user can change email.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (state.status !== "done" || state.outcome !== "forward") return;
    const url = state.targets[0]?.loginUrl;
    if (!url) return;
    if (forwardedUrlRef.current === url) return;
    forwardedUrlRef.current = url;
    window.location.assign(url);
  }, [state]);

  return (
    <div className="space-y-5">
      {showForm ? (
        <form action={formAction} className="space-y-4">
          <div className="space-y-1.5 text-left">
            <label
              htmlFor="router-email"
              className="text-sm font-medium text-white"
            >
              Email address
            </label>
            <input
              id="router-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              className="w-full rounded-md border border-white/20 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/45 focus:outline-none"
            />
          </div>
          {state.status === "invalid" || state.status === "rate_limited" ? (
            <p className="text-sm text-amber-300" role="alert">
              {state.message}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={pending}
            className="vs-btn-solid w-full justify-center"
          >
            {pending ? "Continuing…" : "Continue"}
          </button>
          <p className="text-center text-xs text-[var(--text-muted)]">
            No password — we&apos;ll email a sign-in link next.
          </p>
        </form>
      ) : null}

      {state.status === "done" ? (
        <div className="space-y-4" data-testid="router-result">
          {state.outcome === "none" ? (
            <div className="space-y-3 text-left">
              <p className="text-sm text-white">
                We couldn&apos;t complete sign-in for{" "}
                <span className="font-medium">{state.email}</span>.
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                If your company already works with VirtualSpace, request
                access. If you capture spaces for work, you can also apply to
                join as a Spatialist.
              </p>
              <div className="flex flex-col gap-2">
                <a
                  href={requestAccessUrl(state.email)}
                  className="vs-btn-solid justify-center"
                >
                  Request access
                </a>
                <a
                  href={SPATIALIST_APPLY_URL}
                  className="vs-btn-outline justify-center"
                >
                  Become a Spatialist
                </a>
              </div>
            </div>
          ) : null}

          {forwarding ? (
            <div className="space-y-3 text-center">
              <p className="text-sm text-white">Continuing…</p>
              <p className="text-sm text-[var(--text-secondary)]">
                Taking you to sign in.
              </p>
              <a
                href={state.targets[0]!.loginUrl}
                className="vs-btn-solid inline-flex justify-center"
              >
                Continue
              </a>
            </div>
          ) : null}

          {state.outcome === "choose" ? (
            <div className="space-y-3 text-left">
              <p className="text-sm text-white">Continue as:</p>
              <div className="flex flex-col gap-2">
                {state.targets.map((target) => (
                  <a
                    key={target.portal}
                    href={target.loginUrl}
                    className="vs-card block border-white/15 bg-black/35 p-4 text-left hover:border-white/40"
                  >
                    <span className="vs-heading block text-base text-white">
                      {target.label}
                    </span>
                    <span className="text-sm text-[var(--text-secondary)]">
                      {target.description}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ) : null}

          <p className="text-center text-xs text-[var(--text-muted)]">
            Wrong email?{" "}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline underline-offset-4 hover:text-white"
            >
              Start over
            </button>
          </p>
        </div>
      ) : null}
    </div>
  );
}
